import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { transactionsAPI, usersAPI } from '../../api';
import { fetchOrganizationById, checkDashboardAccess } from '../../ducks/organizations.duck';
import config from '../../config';

const getSubscriptionUsers = subscriptions =>
  subscriptions.filter(s => s.userId).map(s => s.userId);

// ================ Action types ================ //

export const FETCH_SUBSCRIPTION_USERS_REQUEST = 'app/MembersPage/FETCH_SUBSCRIPTION_USERS_REQUEST';
export const FETCH_SUBSCRIPTION_USERS_SUCCESS = 'app/MembersPage/FETCH_SUBSCRIPTION_USERS_SUCCESS';
export const FETCH_SUBSCRIPTION_USERS_ERROR = 'app/MembersPage/FETCH_SUBSCRIPTION_USERS_ERROR';

export const FETCH_TRANSACTIONS_REQUEST = 'app/MembersPage/FETCH_TRANSACTIONS_REQUEST';
export const FETCH_TRANSACTIONS_SUCCESS = 'app/MembersPage/FETCH_TRANSACTIONS_SUCCESS';
export const FETCH_TRANSACTIONS_ERROR = 'app/MembersPage/FETCH_TRANSACTIONS_ERROR';

export const FETCH_SUBSCRIPTIONS_SUCCESS = 'app/MembersPage/FETCH_SUBSCRIPTIONS_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  fetchSubscriptionUsersInProgress: false,
  fetchSubscriptionUsersLoaded: false,
  fetchSubscriptionUsersError: null,
  subscriptionUserIds: [],
  subscriptions: [],
  fetchTransactionsInProgress: false,
  fetchTransactionsError: null,
  transactionIds: [],
};

const resultIds = data => data.data.map(l => l.id);

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

export default function membersPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SUBSCRIPTION_USERS_REQUEST:
      return {
        ...state,
        fetchSubscriptionUsersInProgress: true,
        fetchSubscriptionUsersError: null,
      };
    case FETCH_SUBSCRIPTION_USERS_SUCCESS:
      return {
        ...state,
        fetchSubscriptionUsersInProgress: false,
        fetchSubscriptionUsersLoaded: true,
        fetchSubscriptionUsersError: null,
        subscriptionUserIds: resultIds(payload.data).filter(id =>
          payload.userIds.includes(id.uuid)
        ),
      };
    case FETCH_SUBSCRIPTION_USERS_ERROR:
      return {
        ...state,
        fetchSubscriptionUsersInProgress: false,
        fetchSubscriptionUsersError: payload,
      };

    case FETCH_TRANSACTIONS_REQUEST:
      return {
        ...state,
        fetchTransactionsInProgress: true,
        fetchTransactionsError: null,
      };
    case FETCH_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        transactionIds: entityRefs(payload.transactions),
        fetchTransactionsInProgress: false,
        fetchTransactionsError: null,
      };
    case FETCH_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchTransactionsInProgress: false,
        fetchTransactionsError: payload,
      };

    case FETCH_SUBSCRIPTIONS_SUCCESS:
      return {
        ...state,
        subscriptions: payload.subscriptions,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchSubscriptionUsersRequest = () => ({
  type: FETCH_SUBSCRIPTION_USERS_REQUEST,
});
export const fetchSubscriptionUsersSuccess = (response, userIds) => ({
  type: FETCH_SUBSCRIPTION_USERS_SUCCESS,
  payload: { data: response.data, userIds },
});
export const fetchSubscriptionUsersError = e => ({
  type: FETCH_SUBSCRIPTION_USERS_ERROR,
  error: true,
  payload: e,
});

export const fetchTransactionsRequest = () => ({
  type: FETCH_TRANSACTIONS_REQUEST,
});
export const fetchTransactionsSuccess = transactions => ({
  type: FETCH_TRANSACTIONS_SUCCESS,
  payload: { transactions },
});
export const fetchTransactionsError = e => ({
  type: FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

export const fetchSubscriptionsSuccess = subscriptions => ({
  type: FETCH_SUBSCRIPTIONS_SUCCESS,
  payload: { subscriptions },
});

// ================ Thunks ================ //

// One page of the organization's sales: the subscribers panel picks each customer's out of it. Past
// 100 transactions the panel's totals undercount, until this moves to the ledger.
export const fetchTransactions = selectedOrganization => async (dispatch, getState, sdk) => {
  dispatch(fetchTransactionsRequest());

  try {
    const response = await transactionsAPI.query({
      meta_organizationId: selectedOrganization.id,
      only: 'sale',
      include: ['customer'],
      perPage: 100,
    });

    dispatch(addMarketplaceEntities(response.data));
    dispatch(fetchTransactionsSuccess(response.data.data.data));
  } catch (e) {
    dispatch(fetchTransactionsError(storableError(e)));
  }
};

export const fetchSubscriptionUsers = orgId => async (dispatch, getState, sdk) => {
  dispatch(fetchSubscriptionUsersRequest());

  const selectedOrganization = await dispatch(fetchOrganizationById(orgId));

  return sdk.currentUser.show().then(async userResponse => {
    const authorId = userResponse.data.data.id;

    return sdk.listings
      .query({
        authorId: selectedOrganization ? selectedOrganization.creatorId : authorId,
        pub_type: config.listingTypes['live-room'],
        pub_organizationId: selectedOrganization ? selectedOrganization.id : '',
      })
      .then(async response => {
        const listings = response.data.data;

        const listingSubscriptions = listings
          .filter(l => l.attributes.metadata.subscriptions)
          .map(l => l.attributes.metadata.subscriptions)
          .flat(1);
        const subscriptionUserIds = Array.from(new Set(getSubscriptionUsers(listingSubscriptions)));

        try {
          const usersResponse = await usersAPI.subscriberProfiles(
            subscriptionUserIds,
            selectedOrganization ? selectedOrganization.id : null
          );
          dispatch(addMarketplaceEntities(usersResponse.data));
          dispatch(fetchSubscriptionsSuccess(listingSubscriptions));
          dispatch(fetchSubscriptionUsersSuccess(usersResponse.data, subscriptionUserIds));
          if (subscriptionUserIds.length > 0) {
            dispatch(fetchTransactions(selectedOrganization));
          }
          return usersResponse;
        } catch (e) {
          return dispatch(fetchSubscriptionUsersError(storableError(e)));
        }
      });
  });
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  try {
    // Check dashboard access permissions first
    const accessCheck = await dispatch(checkDashboardAccess('MembersPage'));

    if (!accessCheck.canAccess) {
      console.warn('Members page access denied:', accessCheck.reason);
      throw new Error(`Access denied: ${accessCheck.reason}`);
    }

    // Get selected organization with permissions
    const selectedOrganization = await dispatch(fetchOrganizationById(params.orgId));

    if (!selectedOrganization) {
      throw new Error('No accessible organization found');
    }

    // The subscribers come with their emails: subscriberProfiles.js requires the same permission
    if (!selectedOrganization.permissions?.includes('SEND_NEWSLETTERS')) {
      console.warn('User lacks member dashboard viewing permissions');
      throw new Error('Insufficient permissions to view members');
    }

    return dispatch(fetchSubscriptionUsers(params.orgId));
  } catch (error) {
    console.error('Error loading members data:', error);
    throw error;
  }
};
